import React, { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { CheckIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from "@radix-ui/react-icons";
import { toast } from "react-hot-toast";
import TooltipWrapper from "../TooltipWrapper";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import StripeLoader from "./StripeLoader";
import MessageDraftLoader from "./loader/MessageDraftLoader";
import { messageLength, messageTones } from "../../constant";

import { Check } from "lucide-react";
import { Checkbox } from "../ui/checkbox";
// import { useCaptureEvent } from "../hooks/useCaptureEvent";
import { createAIMessage } from "../../apis/ext";
import { useCaptureEvent } from "../../hooks/useCaptureEvent";
import Image from "../Tags/Image";
export default function MessageDrafter({ activeWorkspace, person_id }) {
  const captureEvent = useCaptureEvent();
  const [messageData, setMessageData] = useState({});
  const [messageType, setMessageType] = useState("");
  const [currentTone, setCurrentTone] = useState("Professional");
  const [currentLength, setCurrentLength] = useState("Medium");

  const [stepLoaderStarted, setStepLoaderStarted] = useState(false);

  const createAIMessageFn = useMutation("create_ai_message", createAIMessage, {
    onSuccess(data, variables, context) {
      console.log(data);
      setStepLoaderStarted(false);
      setMessageData(data);
      setCurrentMessageIndex(0);
      setCurrentMessageIndexBody(0);
      if (!Array.isArray(data.messages) || data.messages.length < 3) {
        toast.error("Something went wrong! Please try again.");
        return;
      }

      // Extract details from the first 3 messages, ensuring that missing subject or body are handled
      const messageDetails = data.messages
        .slice(0, 3)
        .map((msg, index) => {
          const subjectKey = `message_${index + 1}_subject`;
          const bodyKey = `message_${index + 1}_body`;
          const subjectValue = msg.subject || "Subject not available";
          const bodyValue = msg.body ? msg.body.slice(0, 2000) : "Body not available"; // Slicing the body to the first 2000 characters

          return {
            [subjectKey]: subjectValue,
            [bodyKey]: bodyValue,
          };
        })
        .reduce((acc, msgDetails) => ({ ...acc, ...msgDetails }), {});

      captureEvent("EXE_Engage_GeneratedMessageTxt", {
        message_type: variables?.data?.message_type, // 'Connection Note' or 'Outreach'
        message_tone: variables?.data?.message_tone,
        message_length: variables?.data?.message_length,
        number_of_messages: data.messages.length,
        ...messageDetails, // Spread the message details into the event properties
      });
    },
    onError(error) {
      console.log(error);
      setStepLoaderStarted(false);
    },
  });

  const handleCreateAIMessage = (type = "") => {
    setStepLoaderStarted(true);
    if (createAIMessageFn.isLoading) {
      return;
    } else {
      createAIMessageFn.mutate({
        data: {
          person_id: person_id,
          message_type: type ? type : messageType,
          message_tone: currentTone,
          message_length: currentLength,
        },
        options: {
          headers: { "X-Workspace-Id": activeWorkspace?.id },
        },
      });
    }
  };

  useEffect(() => {
    if (messageData && messageData.messages && messageData.messages.length > 0) {
      handleCreateAIMessage();
    }
  }, [currentLength, currentTone]);

  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [currentMessageIndexBody, setCurrentMessageIndexBody] = useState(0);
  const handleUpdateCurrentMessageIndex = (prev) => {
    if (prev) {
      console.log({ currentMessageIndex, ar: messageData.messages.length });
      if (currentMessageIndex > 0) {
        setCurrentMessageIndex((prevIndex) =>
          prevIndex - 1 >= 0 ? prevIndex - 1 : messageData?.messages?.length - 1
        );
      }
    } else {
      console.log({ currentMessageIndex, ar: messageData.messages.length });
      if (currentMessageIndex < messageData.messages.length - 1) {
        setCurrentMessageIndex((prevIndex) =>
          prevIndex + 1 >= 0 ? prevIndex + 1 : messageData?.messages?.length - 1
        );
      }
    }
  };
  const handleUpdateCurrentMessageIndexBody = (prev) => {
    if (prev) {
      console.log({ currentMessageIndexBody, ar: messageData.messages.length });
      if (currentMessageIndexBody > 0) {
        setCurrentMessageIndexBody((prevIndex) =>
          prevIndex - 1 >= 0 ? prevIndex - 1 : messageData?.messages?.length - 1
        );
      }
    } else {
      console.log({ currentMessageIndexBody, ar: messageData.messages.length });
      if (currentMessageIndexBody < messageData.messages.length - 1) {
        setCurrentMessageIndexBody((prevIndex) =>
          prevIndex + 1 >= 0 ? prevIndex + 1 : messageData?.messages?.length - 1
        );
      }
    }
  };

  const [isCopied, setIsCopied] = useState(false);

  const [showCopy, setShowCopy] = useState(false);
  const [hoverType, setHoverType] = useState("");
  const [isSubjectCopied, setIsSubjectCopied] = useState(false);
  const [isBodyCopied, setIsBodyCopied] = useState(false);

  // Reset copy status with clearable timeouts
  const resetCopyStatus = (setCopiedState) => {
    clearTimeout(window.copyTimeout); // Assume window.copyTimeout is globally accessible for this example
    window.copyTimeout = setTimeout(() => {
      setCopiedState(false);
    }, 1000);
  };
  const copyText = (event, data, type) => {
    event.stopPropagation();
    event.preventDefault();
    const copiedText = data.replace(/\n/g, "\r\n");
    let input = document.createElement("textarea");
    input.value = copiedText;
    input.setAttribute("readonly", "");
    input.style.position = "absolute";
    input.style.left = "-9999px";
    document.body.appendChild(input);
    input.select();
    let result = document.execCommand("copy");
    document.body.removeChild(input);
    if (type === "subject") {
      setIsSubjectCopied(true);
      resetCopyStatus(setIsSubjectCopied);
    } else if (type === "body") {
      setIsBodyCopied(true);
      resetCopyStatus(setIsBodyCopied);
    }

    captureEvent("EXE_Engage_CopyMessage", {
      type: type,
      value: data,
    });

    return result;
  };
  const handleShowCopy = (mouseleave) => {
    if (mouseleave) {
      setShowCopy(false);
    } else {
      setShowCopy(true);
    }
  };

  return (
    <>
      {messageData && messageData.messages && messageData.messages.length > 0 && messageType !== "" ? (
        <div className="mt-4">
          <div className="flex justify-between items-center">
            <button
              className={`inline-flex items-center ${
                createAIMessageFn.isLoading ? "pointer-events-none cursor-no-drop" : ""
              }`}
              onClick={() => {
                setMessageData({});
                setMessageType("");
                setStepLoaderStarted(false);
                captureEvent("EXE_Engage_BackBtn");
              }}
            >
              {/* <ChevronLeftIcon /> */}
              <svg width="60" height="20" viewBox="0 0 60 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clip-path="url(#clip0_2722_75496)">
                  <path
                    d="M2.5 7.50033H13.75C15.8211 7.50033 17.5 9.17926 17.5 11.2503C17.5 13.3214 15.8211 15.0003 13.75 15.0003H10M2.5 7.50033L5.83333 4.16699M2.5 7.50033L5.83333 10.8337"
                    stroke="var(--gray-600)"
                    stroke-width="1.66667"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M27.0043 15V4.81818H30.902C31.6378 4.81818 32.2493 4.93419 32.7365 5.16619C33.227 5.39489 33.5933 5.7081 33.8352 6.10582C34.0805 6.50355 34.2031 6.95431 34.2031 7.4581C34.2031 7.8724 34.1236 8.22704 33.9645 8.52202C33.8054 8.81368 33.5916 9.05066 33.3232 9.23295C33.0547 9.41525 32.7547 9.54616 32.4233 9.62571V9.72514C32.7846 9.74503 33.1309 9.85606 33.4624 10.0582C33.7971 10.2571 34.0705 10.5388 34.2827 10.9034C34.4948 11.268 34.6009 11.7088 34.6009 12.2259C34.6009 12.7528 34.4732 13.2268 34.218 13.6477C33.9628 14.0653 33.5784 14.3951 33.0646 14.6371C32.5509 14.879 31.9046 15 31.1257 15H27.0043ZM28.8487 13.4588H30.8324C31.5019 13.4588 31.9841 13.3312 32.2791 13.076C32.5774 12.8175 32.7266 12.486 32.7266 12.0817C32.7266 11.7801 32.652 11.5083 32.5028 11.2663C32.3537 11.0211 32.1416 10.8288 31.8665 10.6896C31.5914 10.5471 31.2633 10.4759 30.8821 10.4759H28.8487V13.4588ZM28.8487 9.14844H30.6733C30.9915 9.14844 31.2782 9.09044 31.5334 8.97443C31.7886 8.85511 31.9891 8.68774 32.1349 8.4723C32.2841 8.25355 32.3587 7.99503 32.3587 7.69673C32.3587 7.30232 32.2195 6.97751 31.9411 6.7223C31.666 6.46709 31.2566 6.33949 30.7131 6.33949H28.8487V9.14844ZM38.3233 15.1541C37.8394 15.1541 37.4036 15.0679 37.0158 14.8956C36.6313 14.7199 36.3264 14.4614 36.101 14.12C35.879 13.7786 35.7679 13.3577 35.7679 12.8572C35.7679 12.4264 35.8475 12.0701 36.0066 11.7884C36.1657 11.5066 36.3828 11.2812 36.6578 11.1122C36.9329 10.9432 37.2428 10.8156 37.5875 10.7294C37.9355 10.6399 38.2952 10.5753 38.6664 10.5355C39.1138 10.4891 39.4767 10.4477 39.7551 10.4112C40.0336 10.3714 40.2357 10.3118 40.3617 10.2322C40.4909 10.1494 40.5556 10.0218 40.5556 9.84943V9.8196C40.5556 9.44508 40.4445 9.15507 40.2225 8.94957C40.0004 8.74408 39.6806 8.64134 39.263 8.64134C38.8221 8.64134 38.4725 8.73745 38.214 8.92969C37.9587 9.12192 37.7864 9.34896 37.6969 9.6108L36.0165 9.37216C36.1491 8.90814 36.3678 8.52036 36.6728 8.20881C36.9777 7.89394 37.3506 7.65862 37.7914 7.50284C38.2322 7.34375 38.7194 7.2642 39.253 7.2642C39.6209 7.2642 39.9872 7.30729 40.3517 7.39347C40.7163 7.47964 41.0494 7.62216 41.351 7.82102C41.6526 8.01657 41.8946 8.28338 42.0769 8.62145C42.2625 8.95952 42.3553 9.3821 42.3553 9.8892V15H40.6252V13.951H40.5655C40.4561 14.1631 40.302 14.362 40.1032 14.5476C39.9076 14.7299 39.6607 14.8774 39.3624 14.9901C39.0674 15.0994 38.7211 15.1541 38.3233 15.1541ZM38.7907 13.8317C39.1519 13.8317 39.4651 13.7604 39.7303 13.6179C39.9954 13.4721 40.1993 13.2798 40.3418 13.0412C40.4876 12.8026 40.5605 12.5424 40.5605 12.2607V11.3608C40.5042 11.4072 40.4081 11.4503 40.2722 11.4901C40.1396 11.5298 39.9905 11.5646 39.8248 11.5945C39.659 11.6243 39.495 11.6508 39.3326 11.674C39.1702 11.6972 39.0293 11.7171 38.91 11.7337C38.6415 11.7701 38.4012 11.8298 38.1891 11.9126C37.977 11.9955 37.8096 12.1115 37.687 12.2607C37.5643 12.4065 37.503 12.5954 37.503 12.8274C37.503 13.1589 37.624 13.4091 37.8659 13.5781C38.1079 13.7472 38.4161 13.8317 38.7907 13.8317ZM47.4959 15.1491C46.7336 15.1491 46.079 14.9818 45.5321 14.647C44.9886 14.3123 44.5693 13.8499 44.2743 13.2599C43.9827 12.6667 43.8368 11.9839 43.8368 11.2116C43.8368 10.4361 43.986 9.75166 44.2843 9.15838C44.5826 8.56179 45.0035 8.09777 45.5471 7.76633C46.0939 7.43158 46.7402 7.2642 47.486 7.2642C48.1058 7.2642 48.6543 7.37855 49.1316 7.60724C49.6122 7.83262 49.995 8.15246 50.28 8.56676C50.565 8.97775 50.7275 9.45833 50.7672 10.0085H49.0471C48.9775 9.64062 48.8117 9.33404 48.5499 9.08878C48.2914 8.8402 47.945 8.71591 47.5108 8.71591C47.1429 8.71591 46.8198 8.81534 46.5414 9.0142C46.263 9.20975 46.0459 9.49148 45.8901 9.85938C45.7376 10.2273 45.6614 10.6681 45.6614 11.1818C45.6614 11.7022 45.7376 12.1496 45.8901 12.5241C46.0426 12.8954 46.2563 13.1821 46.5314 13.3842C46.8098 13.5831 47.1363 13.6825 47.5108 13.6825C47.776 13.6825 48.013 13.6328 48.2218 13.5334C48.4339 13.4306 48.6112 13.2831 48.7537 13.0909C48.8962 12.8987 48.994 12.665 49.0471 12.3899H50.7672C50.7241 12.9302 50.565 13.4091 50.29 13.8267C50.0149 14.241 49.6403 14.5658 49.1664 14.8011C48.6924 15.0331 48.1356 15.1491 47.4959 15.1491ZM53.8857 12.6037L53.8807 10.4311H54.169L56.9134 7.36364H59.0163L55.6406 11.1222H55.2678L53.8857 12.6037ZM52.245 15V4.81818H54.0447V15H52.245ZM57.0376 15L54.5518 11.5249L55.7649 10.2571L59.1903 15H57.0376Z"
                    fill="var(--gray-600)"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_2722_75496">
                    <rect width="60" height="20" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger className="inline-flex items-center focus:outline-none border border-input rounded-[8px] p-2.5">
                {/* <img
                  src="images/ext/horn.svg"
                  height={16}
                  width={16}
                  className="mr-1 no-invert"
                /> */}
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15 8.33326L11.6667 4.99993M2.08331 17.9166L4.90362 17.6032C5.24819 17.5649 5.42048 17.5458 5.58152 17.4937C5.72439 17.4474 5.86035 17.3821 5.98572 17.2994C6.12702 17.2062 6.2496 17.0836 6.49475 16.8385L17.5 5.83326C18.4205 4.91279 18.4205 3.4204 17.5 2.49993C16.5795 1.57945 15.0871 1.57945 14.1667 2.49992L3.16142 13.5052C2.91627 13.7503 2.79369 13.8729 2.70051 14.0142C2.61784 14.1396 2.55249 14.2755 2.50624 14.4184C2.45411 14.5794 2.43497 14.7517 2.39668 15.0963L2.08331 17.9166Z"
                    stroke="var(--gray-700)"
                    stroke-width="1.66667"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <span className="mx-1.5 text-sm">Tone & Length</span>
                <ChevronDownIcon width={20} height={20} />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="min-w-[170px] rounded-[8px]" align="end">
                <DropdownMenuLabel className="p-2 pb-1 text-[13px] font-semibold flex items-center space-x-2">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5.33337 9.33301C5.33337 9.33301 6.33337 10.6663 8.00004 10.6663C9.66671 10.6663 10.6667 9.33301 10.6667 9.33301M10 5.99967H10.0067M6.00004 5.99967H6.00671M14.6667 7.99967C14.6667 11.6816 11.6819 14.6663 8.00004 14.6663C4.31814 14.6663 1.33337 11.6816 1.33337 7.99967C1.33337 4.31778 4.31814 1.33301 8.00004 1.33301C11.6819 1.33301 14.6667 4.31778 14.6667 7.99967ZM10.3334 5.99967C10.3334 6.18377 10.1841 6.33301 10 6.33301C9.81595 6.33301 9.66671 6.18377 9.66671 5.99967C9.66671 5.81558 9.81595 5.66634 10 5.66634C10.1841 5.66634 10.3334 5.81558 10.3334 5.99967ZM6.33337 5.99967C6.33337 6.18377 6.18414 6.33301 6.00004 6.33301C5.81595 6.33301 5.66671 6.18377 5.66671 5.99967C5.66671 5.81558 5.81595 5.66634 6.00004 5.66634C6.18414 5.66634 6.33337 5.81558 6.33337 5.99967Z"
                      stroke="var(--gray-500)"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <span>Tone</span>
                </DropdownMenuLabel>
                {messageTones &&
                  messageTones.length > 0 &&
                  messageTones.map((tone) => (
                    <DropdownMenuItem
                      className={`py-2.5 cursor-pointer space-x-2 ${currentTone === tone && "bg-blue-100"} ${
                        createAIMessageFn.isLoading ? "pointer-events-none" : ""
                      }`}
                      onClick={() => {
                        setCurrentTone(tone);
                        captureEvent("EXE_Engage_Tone&LengthBtn", {
                          tone: tone,
                          length: currentLength,
                        });
                      }}
                    >
                      <Checkbox checked={currentTone === tone} />

                      <span>{tone}</span>
                    </DropdownMenuItem>
                  ))}
                <div className="h-[1px] bg-border -mx-2 my-2"></div>
                <DropdownMenuLabel className="p-2 pb-1 text-[13px] font-semibold flex items-center space-x-2">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11.6667 7.33333H8.33333M11.6667 10H8.33333M11.6667 4.66667H8.33333M6 2L6 14M5.2 2H10.8C11.9201 2 12.4802 2 12.908 2.21799C13.2843 2.40973 13.5903 2.71569 13.782 3.09202C14 3.51984 14 4.0799 14 5.2V10.8C14 11.9201 14 12.4802 13.782 12.908C13.5903 13.2843 13.2843 13.5903 12.908 13.782C12.4802 14 11.9201 14 10.8 14H5.2C4.07989 14 3.51984 14 3.09202 13.782C2.71569 13.5903 2.40973 13.2843 2.21799 12.908C2 12.4802 2 11.9201 2 10.8V5.2C2 4.07989 2 3.51984 2.21799 3.09202C2.40973 2.71569 2.71569 2.40973 3.09202 2.21799C3.51984 2 4.0799 2 5.2 2Z"
                      stroke="var(--gray-500)"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <span>Length</span>
                </DropdownMenuLabel>
                {messageLength &&
                  messageLength.length > 0 &&
                  messageLength.map((el) => (
                    <DropdownMenuItem
                      className={`py-2 cursor-pointer space-x-2 ${currentLength === el && "bg-blue-100"} ${
                        createAIMessageFn.isLoading ? "pointer-events-none" : ""
                      }`}
                      onClick={() => {
                        setCurrentLength(el);
                        captureEvent("EXE_Engage_Tone&LengthBtn", {
                          tone: currentTone,
                          length: el,
                        });
                      }}
                    >
                      <Checkbox checked={currentLength === el} />
                      <span>{el}</span>
                    </DropdownMenuItem>
                  ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="mt-4  pr-8 slide-in   p-4 border rounded-[14px] overflow-hidden border-border  bg-background relative">
            <div className="absolute left-0 top-0 w-full">
              {createAIMessageFn.isLoading && <StripeLoader />}
            </div>
            {messageData.messages[currentMessageIndex].subject && (
              <>
                <div className="flex justify-between mb-2">
                  <span className="font-bold block text-gray-900 text-sm">Subject</span>
                  <div className="inline-flex rounded-[8px] overflow-hidden  items-center">
                    <button
                      className={`  flex items-center justify-center h-8   ${
                        currentMessageIndex === 0 ? "pointer-events-none opacity-50" : ""
                      }`}
                      onClick={() => handleUpdateCurrentMessageIndex(true)}
                    >
                      <ChevronLeftIcon width={20} height={20} />
                    </button>
                    <span className="text-sm mx-2">
                      {currentMessageIndex + 1}/{messageData.messages.length}
                    </span>
                    <button
                      className={` flex items-center justify-center h-8 ${
                        currentMessageIndex === messageData.messages.length - 1
                          ? "pointer-events-none opacity-50"
                          : ""
                      }`}
                      onClick={() => handleUpdateCurrentMessageIndex()}
                    >
                      <ChevronRightIcon width={20} height={20} />
                    </button>
                  </div>
                </div>
                <h2
                  className="text-[14px] leading-7  cursor-pointer relative "
                  onMouseEnter={() => {
                    handleShowCopy(false);
                    setHoverType("subject");
                  }}
                  onMouseLeave={() => {
                    handleShowCopy(true);
                    setHoverType("");
                  }}
                  onClick={(e) => copyText(e, messageData.messages[currentMessageIndex].subject, "subject")}
                >
                  {showCopy && hoverType === "subject" && isSubjectCopied && (
                    <Image src="/images/ext/green-check-line.svg" className="absolute -right-5 top-0 no-invert" />
                  )}
                  <TooltipWrapper
                    content={isSubjectCopied ? "Copied" : "Click to copy subject"}
                    className="text-[14px] leading-7 text-left cursor-pointer whitespace-pre-line text-gray-900"
                  >
                    {messageData.messages[currentMessageIndex].subject}
                  </TooltipWrapper>
                </h2>
              </>
            )}
            <div className="h-[1px] bg-border my-6 max-w-[80%] mx-auto"></div>
            {messageData.messages[currentMessageIndexBody].body && (
              <>
                <div className="flex justify-between mb-2">
                  <span className="font-bold block text-gray-900 text-sm">Body</span>
                  <div className="inline-flex rounded-[8px] overflow-hidden  items-center">
                    <button
                      className={`  flex items-center justify-center h-8   ${
                        currentMessageIndexBody === 0 ? "pointer-events-none opacity-50" : ""
                      }`}
                      onClick={() => handleUpdateCurrentMessageIndexBody(true)}
                    >
                      <ChevronLeftIcon width={20} height={20} />
                    </button>
                    <span className="text-sm mx-2">
                      {currentMessageIndexBody + 1}/{messageData.messages.length}
                    </span>
                    <button
                      className={` flex items-center justify-center h-8 ${
                        currentMessageIndexBody === messageData.messages.length - 1
                          ? "pointer-events-none opacity-50"
                          : ""
                      }`}
                      onClick={() => handleUpdateCurrentMessageIndexBody()}
                    >
                      <ChevronRightIcon width={20} height={20} />
                    </button>
                  </div>
                </div>
                <p
                  className="mt-4 text-[14px] leading-7 cursor-pointer relative"
                  onMouseEnter={() => {
                    handleShowCopy(false);
                    setHoverType("body");
                  }}
                  onMouseLeave={() => {
                    handleShowCopy(true);
                    setHoverType("");
                  }}
                  onClick={(e) => copyText(e, messageData.messages[currentMessageIndexBody].body, "body")}
                >
                  {showCopy && hoverType === "body" && isBodyCopied && (
                    <Image src="/images/ext/green-check-line.svg" className="absolute -right-5 top-0 no-invert" />
                  )}
                  <TooltipWrapper
                    content={isBodyCopied ? "Copied" : "Click to copy body"}
                    className="text-[14px] leading-7 text-left cursor-pointer whitespace-pre-line"
                  >
                    {messageData.messages[currentMessageIndexBody].body}
                  </TooltipWrapper>
                </p>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="  p-4 border rounded-[14px] overflow-hidden border-border  bg-background relative">
          <h1 className="text-[16px] font-bold flex items-center justify-center">
            <Image src="/images/ext/engage/msg.svg" alt="Message" className="mr-2 no-invert" /> Personalised Messages
            with
            <span className="text-brand-600 ml-1"> AI</span>
          </h1>
          {/* <p className="text-[12px] text-gray-600 text-center mt-1">
            Generate outreach messages through AI
          </p> */}
          <div className="text-center my-7">
            <Image src="/images/ext/engage/plane.svg" alt="Message" className="inline-block no-invert" />
          </div>
          {!stepLoaderStarted && (
            <div>
              {/* <p className="text-[12px] text-gray-600 mb-2">
                I want to generate a:
              </p> */}
              <div className="space-y-3">
                <button
                  onClick={() => {
                    setMessageType("Connection Note");
                    handleCreateAIMessage("Connection Note");
                    captureEvent("EXE_Engage_LinkedInConnectionRequestBtn");
                  }}
                  className="text-brand-700 bg-background rounded-[8px] text-[12px] border border-brand-300 h-9 px-[7px] flex w-full items-center justify-center"
                >
                  <Image src="/images/ext/engage/linkedin.svg" alt="Message" className="inline-block mr-1 no-invert" />{" "}
                  Connection Request
                </button>
                <button
                  onClick={() => {
                    setMessageType("Outreach");
                    handleCreateAIMessage("Outreach");
                    captureEvent("EXE_Engage_OutreachMessageBtn");
                  }}
                  className="text-brand-700 bg-background rounded-[8px] text-[12px] border border-brand-300 h-9 px-[7px] flex w-full items-center justify-center"
                >
                  <Image src="/images/ext/engage/email.svg" alt="Message" className="inline-block mr-1 no-invert" />{" "}
                  Outreach message
                </button>
              </div>
            </div>
          )}
          {stepLoaderStarted && <MessageDraftLoader />}
        </div>
      )}
    </>
  );
}
